/**
 * Centralized configuration for the Clients module
 * This allows easy customization per firm/client
 */

export interface ClientsUIConfig {
  // Table settings
  defaultPageSize: number;
  pageSizeOptions: number[];
  enableSorting: boolean;
  enableFiltering: boolean;
  showRowNumbers: boolean;
  enableRowSelection: boolean;
  tableStriped: boolean;
  tableHover: boolean;
  tableBordered: boolean;
  
  // Form settings
  formLayout: 'single-column' | 'two-column' | 'responsive';
  enableContactPersons: boolean;
  enableClientContacts: boolean;
  maxContactPersons: number;
  maxClientContacts: number;
  requiredFieldMarker: string;
  
  // Validation settings
  requireVatNumber: boolean;
  enableVatValidation: boolean;
  requireAddress: boolean;
  requireCountry: boolean;
  
  // Export settings
  enablePdfExport: boolean;
  enableExcelExport: boolean;
  enableCsvExport: boolean;
  defaultExportFormat: 'pdf' | 'excel' | 'csv';
  
  // Search settings
  enableGlobalSearch: boolean;
  searchPlaceholder: string;
  searchDebounceMs: number;
  enableAdvancedFilters: boolean;
  
  // Dialog settings
  dialogWidth: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  enableFullscreenDialog: boolean;
  confirmDelete: boolean;
  confirmArchive: boolean;
  
  // Display settings
  showFriendlyName: boolean;
  showVatDetails: boolean;
  showContactCount: boolean;
  enableClientCards: boolean;
  enableCompactView: boolean;
  
  // Colors and styling
  primaryColor: string;
  secondaryColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  mutedColor: string;
  
  // Button styles
  addButtonVariant: 'default' | 'outline' | 'secondary';
  editButtonVariant: 'default' | 'outline' | 'secondary';
  deleteButtonVariant: 'destructive' | 'outline';
  exportButtonVariant: 'default' | 'outline' | 'secondary';
  
  // Rounding settings
  currencyRounding: number;
  percentageRounding: number;
  
  // Animation settings
  enableAnimations: boolean;
  transitionDuration: string;
  
  // Accessibility
  enableKeyboardShortcuts: boolean;
  highContrastMode: boolean;
  
  // Data display
  dateFormat: string;
  currencyFormat: string;
  numberFormat: string;
  
  // Layout settings
  maxWidth: string;
  cardShadow: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export const defaultClientsConfig: ClientsUIConfig = {
  // Table settings
  defaultPageSize: 25,
  pageSizeOptions: [10, 25, 50, 100],
  enableSorting: true,
  enableFiltering: true,
  showRowNumbers: false,
  enableRowSelection: false,
  tableStriped: false,
  tableHover: true,
  tableBordered: true,
  
  // Form settings
  formLayout: 'two-column',
  enableContactPersons: true,
  enableClientContacts: true,
  maxContactPersons: 10,
  maxClientContacts: 5,
  requiredFieldMarker: '*',
  
  // Validation settings
  requireVatNumber: false,
  enableVatValidation: true,
  requireAddress: false,
  requireCountry: true,
  
  // Export settings
  enablePdfExport: true,
  enableExcelExport: true,
  enableCsvExport: false,
  defaultExportFormat: 'excel',
  
  // Search settings
  enableGlobalSearch: true,
  searchPlaceholder: 'Search clients...',
  searchDebounceMs: 300,
  enableAdvancedFilters: false,
  
  // Dialog settings
  dialogWidth: '2xl',
  enableFullscreenDialog: false,
  confirmDelete: true,
  confirmArchive: true,
  
  // Display settings
  showFriendlyName: true,
  showVatDetails: true,
  showContactCount: false,
  enableClientCards: false,
  enableCompactView: false,
  
  // Colors and styling
  primaryColor: '#3b82f6',
  secondaryColor: '#64748b',
  successColor: '#10b981',
  warningColor: '#f59e0b',
  errorColor: '#ef4444',
  mutedColor: '#6b7280',
  
  // Button styles - Use more subtle defaults
  addButtonVariant: 'default',
  editButtonVariant: 'outline',
  deleteButtonVariant: 'destructive',
  exportButtonVariant: 'outline',
  
  // Rounding settings
  currencyRounding: 2,
  percentageRounding: 2,
  
  // Animation settings - Reduced for consistency
  enableAnimations: true,
  transitionDuration: '150ms',
  
  // Accessibility
  enableKeyboardShortcuts: true,
  highContrastMode: false,
  
  // Data display
  dateFormat: 'DD/MM/YYYY',
  currencyFormat: '€#,##0.00',
  numberFormat: '#,##0.##',
  
  // Layout settings - More neutral defaults
  maxWidth: '1280px',
  cardShadow: 'sm',
};

// Firm-specific configurations - More subtle by default
export const firmClientsConfigs: Record<string, Partial<ClientsUIConfig>> = {
  'law-firm-modern': {
    primaryColor: '#8b5cf6',
    formLayout: 'single-column',
    dialogWidth: 'xl',
    enableClientCards: true,
    // Keep button variants subtle
    addButtonVariant: 'default',
    editButtonVariant: 'outline',
  },
  'law-firm-classic': {
    primaryColor: '#1f2937',
    formLayout: 'two-column',
    enableAnimations: false,
    // More conservative styling
    addButtonVariant: 'outline',
    editButtonVariant: 'outline',
    confirmDelete: true,
    confirmArchive: true,
  },
  'corporate-firm': {
    primaryColor: '#059669',
    defaultPageSize: 50,
    enableAdvancedFilters: true,
    showRowNumbers: true,
    enableRowSelection: true,
    maxContactPersons: 20,
    requireVatNumber: true,
    // Keep styling consistent
    addButtonVariant: 'default',
    editButtonVariant: 'outline',
  },
  'boutique-firm': {
    primaryColor: '#dc2626',
    formLayout: 'single-column',
    enableClientCards: true,
    enableCompactView: true,
    defaultPageSize: 10,
    enableFullscreenDialog: true,
    // Use outline variants for subtlety
    addButtonVariant: 'outline',
    editButtonVariant: 'outline',
  },
};

// Hook to get current configuration
export const useClientsConfig = (firmId?: string): ClientsUIConfig => {
  const firmConfig = firmId ? firmClientsConfigs[firmId] || {} : {};
  
  return {
    ...defaultClientsConfig,
    ...firmConfig,
  };
};

// Configuration validation
export const validateClientsConfig = (config: Partial<ClientsUIConfig>): string[] => {
  const errors: string[] = [];
  
  if (config.defaultPageSize && config.defaultPageSize <= 0) {
    errors.push('Default page size must be greater than 0');
  }
  
  if (config.maxContactPersons && config.maxContactPersons < 1) {
    errors.push('Max contact persons must be at least 1');
  }
  
  if (config.maxClientContacts && config.maxClientContacts < 1) {
    errors.push('Max client contacts must be at least 1');
  }
  
  if (config.searchDebounceMs && config.searchDebounceMs < 0) {
    errors.push('Search debounce must be non-negative');
  }
  
  return errors;
};
